import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Gift } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

export const GiftCreditsButton = ({ vendor }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(5);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const n = Number(credits);
    if (!n || n < 1) { toast({ title: 'Enter at least 1 credit', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const r = await adminApi.giftCredits(vendor.id, n, note.trim());
      toast({ title: 'Credits gifted', description: `${vendor.name || 'Vendor'} now has ${r.balance} credits.` });
      setOpen(false);
      setNote('');
    } catch (err) {
      toast({ title: 'Gift failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-2 py-1 text-xs font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 rounded transition-colors mr-1"
        title="Gift bonus lead credits to this vendor"
        data-testid={`gift-credits-${vendor.id}`}
      >
        Gift
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-purple-600" /> Gift bonus credits</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="gift-credits-dialog">
            <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
              <b>{vendor.name || 'Vendor'}</b>{vendor.vendor_code ? ` · ${vendor.vendor_code}` : ''}
              <span className="block text-xs text-gray-400 mt-0.5">Current balance: {Number(vendor.lead_credits) || 0} credits</span>
            </div>
            <div>
              <Label className="text-xs">Credits to gift (1–1000) *</Label>
              <Input
                type="number" min="1" max="1000" value={credits}
                onChange={(e) => setCredits(e.target.value)}
                data-testid="gift-credits-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Note shown to vendor (optional)</Label>
              <Input
                value={note} maxLength={200}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g., Thanks for being a loyal vendor!"
                data-testid="gift-credits-note"
              />
            </div>
            <p className="text-[11px] text-gray-400">The vendor gets a bell notification and the gift appears in their Credit History.</p>
            <Button type="submit" disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="gift-credits-submit">
              <Gift className="w-4 h-4 mr-1.5" /> {saving ? 'Gifting…' : `Gift ${credits || 0} credits`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GiftCreditsButton;
